import React, { useState } from 'react'
import { graphql, usePaginationFragment } from 'react-relay/hooks'
import { groupBy } from 'lodash'
import createPersistedState from 'use-persisted-state'
import {
  FlexRow,
  FlexColumn,
  Text,
  Anchor,
  Table as Tables,
  TablePagination,
  Dropdown,
  LinkButton,
  Icon,
} from 'elements'
import ReportsTableRow from './ReportsTableRow'
import ReportsHelpBanner from './ReportsHelpBanner'
import { useModals } from 'hooks'
import EmptyState from 'components/common/EmptyState'
import CreateReportModalContainer from '../create/CreateReportModalContainer'
import IntegrationHelpBanner from './IntegrationHelpBanner'
import ConnectionBanner from './ConnectionBanner'

const { Table, Head, HeadRow, HeadCell, Body, Row, Cell } = Tables.Card

const useHelpBannerState = createPersistedState('helpBanner')
const useIntegrationBannerState = createPersistedState('integrationBanner')
const useConnectionBannerState = createPersistedState('connectionBanner')

export default function ReportsTable({ company }) {
  const [includeDeleted, setIncludeDeleted] = useState(false)
  const { openModal } = useModals()
  const [removeHelpBanner, setRemoveHelpBanner] = useHelpBannerState(false)
  const [removeIntegrationBanner, setRemoveIntegrationBanner] = useIntegrationBannerState(false)
  const [removeConnectionBanner, setRemoveConnectionBanner] = useConnectionBannerState(false)

  const { data, refetch, ...paginationProps } = usePaginationFragment(
    graphql`
      fragment ReportsTable_company on Company
        @argumentDefinitions(
          cursor: { type: "Cursor" }
          count: { type: "Int", defaultValue: 10 }
          includeDeleted: { type: "IncludeDeletedOption", defaultValue: NO }
        )
        @refetchable(queryName: "ReportsTablePaginationQuery") {
        reports(
          after: $cursor
          first: $count
          orderBy: CREATED_AT_DESC
          includeDeleted: $includeDeleted
        ) @connection(key: "ReportsTable_reports") {
          edges {
            node {
              id
              status
              errors
              reportIntegrations {
                nodes {
                  integration {
                    kind
                  }
                }
              }
              ...ReportsTableRow_report
            }
          }
        }
      }
    `,
    company,
  )

  const handleCreateReport = () => openModal(CreateReportModalContainer, {}, 'semi')

  const toggleArchived = () => {
    const inc = !includeDeleted
    setIncludeDeleted(inc)

    refetch({ includeDeleted: inc ? 'YES' : 'NO' }, { fetchPolicy: 'store-and-network' })
  }

  // filter out rows that have been permanently deleted or null errors will be thrown
  const edges = (data?.reports?.edges || []).filter((edge) => !!edge.node)

  const hasReports = edges.length > 0
  const hasSuccess = edges.some(({ node }) => node.status === 'SUCCESS')
  const hasError = edges.some(({ node }) => node.status === 'ERROR')

  // show the banner if there’s one or more failed reports and no successful reports
  const needsHelp = hasReports && hasError && !hasSuccess

  const integrationReports = edges
    .filter(({ node }) => node?.reportIntegrations?.nodes?.length)
    .map(({ node }) => node)

  const integrationReportsByKind = groupBy(
    integrationReports,
    (node) => node?.reportIntegrations?.nodes?.[0]?.integration?.kind,
  )

  const completedStatuses = ['SUCCESS', 'ERROR']

  const firstIntReportProcessing = Object.keys(integrationReportsByKind).some((kind) => {
    const reportsOfKind = integrationReportsByKind[kind]

    return reportsOfKind.length === 1 && !completedStatuses.includes(reportsOfKind[0].status)
  })

  const hasConnectionError = edges?.[0]?.node?.errors?.some(
    (error) => error.code === 'response-error',
  )

  const displayIntegrationBanner = removeIntegrationBanner ? false : firstIntReportProcessing
  const displayHelpBanner = removeHelpBanner ? false : needsHelp
  const displayConnectionBanner = removeConnectionBanner ? false : hasConnectionError

  return (
    <>
      {displayHelpBanner ? (
        <ReportsHelpBanner
          onClose={() => setRemoveHelpBanner(true)}
          mb={displayIntegrationBanner ? 'large' : null}
        />
      ) : null}
      {displayIntegrationBanner ? (
        <IntegrationHelpBanner onClose={() => setRemoveIntegrationBanner(true)} />
      ) : null}
      {displayConnectionBanner ? (
        <ConnectionBanner onClose={() => setRemoveConnectionBanner(true)} />
      ) : null}
      <Table spacing="large">
        <Head>
          <HeadRow>
            <HeadCell>Report Name</HeadCell>
            <HeadCell></HeadCell>
            <HeadCell width="180px">Date Created</HeadCell>
            <HeadCell width="140px">Source</HeadCell>
            <HeadCell width="80px">Status</HeadCell>
            <HeadCell width="100px">
              <FlexRow alignItems="center" justifyContent="flex-end">
                <Dropdown
                  position="bottom right"
                  options={[
                    {
                      label: includeDeleted ? 'Hide archived' : 'Show archived',
                      onSelect: toggleArchived,
                    },
                  ]}
                  stopPropagation
                  renderButton={() => (
                    <LinkButton kind="transparent">
                      <Icon
                        size="medium"
                        name="cog"
                        color={includeDeleted ? 'secondary.0' : 'neutral.2'}
                      />
                    </LinkButton>
                  )}
                />
              </FlexRow>
            </HeadCell>
          </HeadRow>
        </Head>
        <Body>
          {hasReports ? (
            edges.map(({ node: report }) => <ReportsTableRow report={report} key={report.id} />)
          ) : (
            <Row>
              <Cell colSpan={6}>
                <FlexColumn p="xxxlarge">
                  <EmptyState
                    message="No reports found."
                    action={
                      <Text color="neutral.0">
                        <Anchor onClick={handleCreateReport}>Create a report</Anchor> or{' '}
                        <Anchor href="https://www.scrutinize.io/howitworks" target="_blank">
                          learn how it works.
                        </Anchor>
                      </Text>
                    }
                  />
                </FlexColumn>
              </Cell>
            </Row>
          )}
        </Body>
        <TablePagination Table={Tables.Card} colSpan={6} {...paginationProps} />
      </Table>
    </>
  )
}
